/**
 * Whether a quotation is long enough for a block quote, by the rules of each style.
 *
 * MLA 9 sets prose off only when it runs to MORE than four lines (MLA Handbook, p. 76), but
 * verse at four lines or more (MLA Style Center, 27 October 2021). A line there is a line of
 * the writer's own typeset page, not a line break in the pasted text (a PDF or web page wraps
 * at its own width), so prose is measured by wrapping the text at the width of a standard MLA
 * page: 12-point Times New Roman across 6.5 inches, about 75 characters. Verse lines are the
 * poet's own and are counted as pasted.
 * Chicago 18 §§12.9–12.10: a hundred words or more, and quotations of two or more paragraphs.
 */

export type BlockQuoteStyle = "apa" | "mla" | "chicago";

/** Characters on one line of 12-point Times New Roman across 6.5 inches (an estimate). */
export const MLA_CHARS_PER_LINE = 75;

const WHITESPACE_RE = /\s+/;
const NEWLINE_RE = /\n/;
const PARAGRAPH_RE = /\n\s*\n/;

export function countWords(text: string): number {
  return text
    .trim()
    .split(WHITESPACE_RE)
    .filter((w) => w.length > 0).length;
}

/** Lines as pasted: the right count for verse, where each line break is the poet's. */
export function countLines(text: string): number {
  if (text.trim().length === 0) {
    return 0;
  }
  return text.trim().split(NEWLINE_RE).length;
}

export function countParagraphs(text: string): number {
  if (text.trim().length === 0) {
    return 0;
  }
  return text
    .trim()
    .split(PARAGRAPH_RE)
    .filter((p) => p.trim().length > 0).length;
}

function wrappedLines(paragraph: string, width: number): number {
  let lines = 0;
  let used = 0;
  for (const word of paragraph.split(WHITESPACE_RE).filter(Boolean)) {
    if (used === 0) {
      lines += 1;
      used = word.length;
    } else if (used + 1 + word.length <= width) {
      used += 1 + word.length;
    } else {
      lines += 1;
      used = word.length;
    }
  }
  return lines;
}

/**
 * Lines the prose would fill on an MLA page. Single line breaks are soft wraps and become
 * spaces; a blank line starts a new paragraph, which begins on a fresh line.
 */
export function estimateProseLines(
  text: string,
  width = MLA_CHARS_PER_LINE
): number {
  let total = 0;
  for (const paragraph of text.trim().split(PARAGRAPH_RE)) {
    total += wrappedLines(paragraph, width);
  }
  return total;
}

/** The line count the MLA rule is applied to: typeset for prose, as pasted for verse. */
export function mlaLineCount(text: string, verse: boolean): number {
  return verse ? countLines(text) : estimateProseLines(text);
}

export function shouldBeBlockQuote(
  text: string,
  style: BlockQuoteStyle,
  verse: boolean
): boolean {
  if (style === "apa") {
    return countWords(text) >= 40;
  }
  if (style === "mla") {
    const lines = mlaLineCount(text, verse);
    return verse ? lines >= 4 : lines > 4;
  }
  return countWords(text) >= 100 || countParagraphs(text) >= 2;
}
